import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { SubdomainService } from '../../_base/subdomain.service';
import { MadAuthService } from '../../mad-auth/mad-auth.service';

/**
 * A value a facet can be ticked with. `null` is the "no value recorded" option, kept
 * distinct from the string 'N/A' so it can never collide with a real value.
 */
export type FacetValue = string | null;

export type FacetType = 'string' | 'number' | 'date';

export type FacetDescriptor = {
    key: string;
    label: string;
    /** Drives the controls: 'number' adds From/To, 'date' adds month/year. */
    type: FacetType;
    /** What this column calls "no value recorded" — 'Unknown' for sub-type, else 'N/A'. */
    emptyLabel: string;
    /** Set when values are stored in m² but shown in the viewer's own unit. */
    unit: string | null;
};

export type FacetRange = {
    min: number | null;
    max: number | null;
};

export type FacetDateParts = {
    month: number | null;
    year: number | null;
};

export type FacetValues = {
    values: FacetValue[];
    /** The facet has more values than the server will list; what came back is a prefix. */
    truncated: boolean;
};

export type FacetValuesResponse = {
    data: { [key: string]: FacetValues };
    facets: FacetDescriptor[];
};

/**
 * Selectable values for the comparable lists' multi-value filters.
 *
 * The server derives them from the table under the filters currently applied, so the
 * list stays server-paginated — nothing here needs the comparables themselves.
 */
@Injectable({
    providedIn: 'root'
})
export class AssetClassFacetService {
    API_COMPARABLES_URL = environment.baseApiUrl + `api/${this.subDomainService.subDomain}/comparable/`;

    /**
     * The resource each asset class type's values are served from. A type absent here has
     * no column filters, and asking for its values is a programming error rather than an
     * empty result — hence the throw in getFacetValues().
     */
    private static readonly RESOURCES: { [acType: number]: string } = {
        1: 'residentials',
        3: 'offices',
        5: 'retail-shops',
        17: 'houses'
    };

    constructor(
        private http: HttpClient,
        private authService: MadAuthService,
        private subDomainService: SubdomainService
    ) {}

    /**
     * Values for every facet of `acType` under `filter`. Each facet is computed with the
     * other filters applied but not its own ticks, so a ticked list never collapses to
     * the one value that was ticked.
     */
    getFacetValues(acType: number, filter: any): Observable<FacetValuesResponse> {
        const resource = AssetClassFacetService.RESOURCES[acType];
        if (!resource) {
            throw new Error(`No comparable filters are defined for asset class type ${acType}`);
        }
        const httpHeaders = this.authService.getAuthHeaders();
        const url = this.API_COMPARABLES_URL + resource + '/filter-values';
        return this.http
            .post<FacetValuesResponse>(url, { filter }, { headers: httpHeaders })
            .pipe(map(response => ({
                data: response && response.data ? response.data : {},
                facets: response && response.facets ? response.facets : []
            })));
    }

    /** Whether this asset class type has column filters at all. */
    static supports(acType: number): boolean {
        return !!AssetClassFacetService.RESOURCES[acType];
    }
}
